import React, { memo, useState } from "react";
import { Link } from "react-router-dom";

/**
 * "Needs attention" card shown under the Recording Quality rows when a result
 * has a quality issue. Styled like the activity-feed cards (rounded, elevated)
 * with an amber info glyph leading the first line of copy so it reads as a
 * gentle prompt, with compact footer actions that deep-link to mode guidance
 * and the repeat flow when available. Renders nothing when there is neither a
 * message nor a hint.
 */
function ResultQualityNotice({
  messages = [],
  hints = [],
  headerTitle = "",
  bodyText = "",
  detailContent = null,
  showDetailsLabel = "",
  hideDetailsLabel = "",
  hintsTitle = "",
  linkTo = "",
  repeatLinkTo = "",
  onLinkClick = null,
  ctaLabel = "",
  repeatCtaLabel = "",
}) {
  const noticeMessages = Array.isArray(messages)
    ? messages.filter(Boolean)
    : [];
  const noticeHints = Array.isArray(hints) ? hints.filter(Boolean) : [];
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (!noticeMessages.length && !noticeHints.length && !headerTitle) {
    return null;
  }

  // The icon pairs with whichever copy leads the card instead of heading the
  // whole text column, so the rest runs full width and no gap opens up beneath
  // the glyph. Hint-only notices promote the hints title into that row.
  const [headerMessage, ...remainingMessages] = noticeMessages;
  const headerUsesHintsTitle =
    !headerMessage && !headerTitle && Boolean(hintsTitle);
  const showFooterGuidanceLink = Boolean(linkTo);
  const showDetailToggle = Boolean(detailContent);
  const accessibleLabel = headerMessage
    ? [headerMessage.title, headerMessage.subtitle].filter(Boolean).join(" ")
    : headerTitle || hintsTitle || "Quality guidance";
  const detailToggleLabel = detailsOpen ? hideDetailsLabel : showDetailsLabel;

  return (
    <div
      className="w-full rounded-2xl bg-card px-2 py-3 shadow flex flex-col gap-2 text-left"
      role="note"
      aria-label={accessibleLabel}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span
            className="material-symbols-outlined text-[22px] leading-none text-amber-500"
            aria-hidden="true"
          >
            info
          </span>

          {headerMessage ? (
            <p className="min-w-0 flex-1 text-sm text-typography leading-relaxed">
              {headerMessage.title} {headerMessage.subtitle}
            </p>
          ) : headerTitle ? (
            <p className="min-w-0 flex-1 text-typography font-medium">
              {headerTitle}
            </p>
          ) : headerUsesHintsTitle ? (
            <p className="min-w-0 flex-1 text-typography font-medium">
              {hintsTitle}
            </p>
          ) : null}
        </div>

        {showDetailToggle ? (
          <button
            type="button"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-greyscale-4 transition-transform hover:text-typography focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            aria-expanded={detailsOpen}
            aria-label={detailToggleLabel}
            onClick={() => setDetailsOpen((open) => !open)}
          >
            <span
              className={[
                "material-symbols-outlined text-[20px] leading-none transition-transform",
                detailsOpen ? "rotate-180" : "",
              ].join(" ")}
              aria-hidden="true"
            >
              {detailsOpen ? "collapse_content" : "expand_content"}
            </span>
          </button>
        ) : null}
      </div>

      {remainingMessages.map((message, index) => (
        <p
          key={`${message.title || "message"}-${index}`}
          className="text-sm text-typography leading-relaxed"
        >
          {message.title} {message.subtitle}
        </p>
      ))}

      {bodyText ? (
        <p className="text-sm text-typography leading-relaxed">{bodyText}</p>
      ) : null}

      {detailContent && detailsOpen ? (
        <div className="pt-1" data-testid="quality-details-content">
          {detailContent}
        </div>
      ) : null}

      {noticeHints.length ? (
        <div className="w-full">
          {hintsTitle && !headerUsesHintsTitle ? (
            <p className="mb-1 text-typography font-medium">{hintsTitle}</p>
          ) : null}
          {noticeHints.length === 1 ? (
            <p className="text-sm text-typography leading-relaxed">
              {noticeHints[0]}
            </p>
          ) : (
            <ul className="list-disc pl-5 flex flex-col gap-1 marker:text-greyscale-3">
              {noticeHints.map((hint) => (
                <li key={hint} className="text-sm text-typography">
                  {hint}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {showFooterGuidanceLink || repeatLinkTo ? (
        <div
          className="mt-1 flex w-full items-center gap-2"
          data-testid="quality-notice-actions"
        >
          {showFooterGuidanceLink ? (
            <Link
              to={linkTo}
              className="inline-flex items-center self-center text-sm font-medium text-primary"
              onClick={onLinkClick || undefined}
            >
              {ctaLabel}
            </Link>
          ) : null}
          {repeatLinkTo ? (
            <Link
              to={repeatLinkTo}
              className="small-primary-button !ml-auto !min-h-8 !px-3 !text-xs"
            >
              {repeatCtaLabel}
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default memo(ResultQualityNotice);
